import type { ModelSalesFunnelStageLeads } from '@app/api/SalesFunnels';

const mergeLeadLists = (
  prevLeads: NonNullable<ModelSalesFunnelStageLeads['Leads']>,
  nextLeads: NonNullable<ModelSalesFunnelStageLeads['Leads']>
) => {
  const prevById = new Map(prevLeads.map((lead) => [lead.Id, lead]));
  // Track whether every lead in the merged result kept its prev reference.
  // True only when the count is identical and every ID was already in prevLeads.
  let isAllPrevRefs = prevLeads.length === nextLeads.length;
  const leads = nextLeads.map((lead) => {
    const prev = prevById.get(lead.Id);
    if (prev !== undefined) return prev;
    isAllPrevRefs = false;
    return lead;
  });
  return { leads, isAllPrevRefs };
};

export const mergeSalesFunnelStageLeads = (
  prevStages: ModelSalesFunnelStageLeads[],
  nextStages: ModelSalesFunnelStageLeads[]
): ModelSalesFunnelStageLeads[] => {
  const stageById = new Map(
    prevStages.map((stage) => [stage.StageId, stage] as const)
  );

  nextStages.forEach((nextStage) => {
    const prevStage = stageById.get(nextStage.StageId);

    if (!prevStage) {
      stageById.set(nextStage.StageId, nextStage);
      return;
    }

    const { leads: mergedLeads, isAllPrevRefs } = mergeLeadLists(
      prevStage.Leads ?? [],
      nextStage.Leads ?? []
    );

    // Return the same object reference when no field changed.
    // This lets the WeakMap stage cache in impl-B reuse the cached
    // LeadsFunnelBoardStage for unchanged stages on load-more and DnD.
    const isUnchanged =
      isAllPrevRefs &&
      prevStage.StageName === nextStage.StageName &&
      prevStage.StageDescription === nextStage.StageDescription &&
      prevStage.Color === nextStage.Color &&
      prevStage.TotalLeads === nextStage.TotalLeads;

    stageById.set(
      nextStage.StageId,
      isUnchanged
        ? prevStage
        : { ...prevStage, ...nextStage, Leads: mergedLeads }
    );
  });

  return Array.from(stageById.values());
};
